import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDrawerMode } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, filter, map, Observable } from 'rxjs';

export interface Breakpoints {
  isDesktop$: Observable<boolean>;
  isTablet$: Observable<boolean>;
  isMobile$: Observable<boolean>;
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly breakpointObs$ = inject(BreakpointObserver);
  private readonly router = inject(Router);
  private readonly DARK_CLASS = 'dark-mode';

  public breakpoints: Breakpoints = {
    isDesktop$: this.breakpointObs$.observe(['(min-width: 1024px)']).pipe(map(x => x.matches)),
    isTablet$: this.breakpointObs$.observe(['(min-width: 768px) and (max-width: 1023.98px)']).pipe(map(x => x.matches)),
    isMobile$: this.breakpointObs$.observe(['(max-width: 767.98px)']).pipe(map(x => x.matches)),
  }

  public readonly isMobile = toSignal(this.breakpoints.isMobile$, { initialValue: false });


  public readonly sideNav$ = new BehaviorSubject<{ open: boolean, mode: MatDrawerMode }>({
    open: false,
    mode: 'over'
  });


  constructor() {
    combineLatest([
      this.breakpointObs$.observe(['(max-width: 1023.98px)']),
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      )
    ]).subscribe(([breakpoints, _]) => {
      this.sideNav$.next({ mode: "over", open: false })
    });
  }

  toggleSideNav(): void {
    const current = this.sideNav$.value;
    this.sideNav$.next({ ...current, open: !current.open });
  }

  closeSideNav(): void {
    const current = this.sideNav$.value;
    if (current.open) {
      this.sideNav$.next({ ...current, open: false });
    }
  }

  enableDarkTheme(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.add(this.DARK_CLASS);
    }
  }

  enableLightTheme(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.remove(this.DARK_CLASS);
    }
  }

  toggleTheme(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.toggle(this.DARK_CLASS);
    }
  }

  isDarkTheme(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      // Safe to use `document`, `window`, `localStorage`, etc.
      return document.body.classList.contains(this.DARK_CLASS);
    }
    return false;
  }
}
